import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Calendar, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ExperienceEntryProps {
  exp: {
    id?: string;
    role: string;
    company: string;
    startDate: string;
    endDate?: string;
    isCurrent: boolean;
    description: string;
  };
}

export function ExperienceEntry({ exp }: ExperienceEntryProps) {
  const { t } = useTranslation(["talent", "common"]);

  return (
    <div className="relative group">
      {/* Icon/Dot */}
      <div className="absolute left-[-32px] top-0 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-gray-100 ring-4 ring-white shrink-0 z-10">
        <Building2 className="w-3 h-3 text-gray-500" />
      </div>

      {/* Content */}
      <Card className="border shadow-sm hover:shadow-md transition-shadow">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
            <div>
              <h4 className="font-bold text-gray-900 text-lg leading-tight mb-1">
                {exp.role}
              </h4>
              <div className="text-primary font-medium text-base">
                {exp.company}
              </div>
            </div>

            <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
              {exp.isCurrent && (
                <Badge
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {t("talent:experience.present")}
                </Badge>
              )}
              <div className="flex items-center text-sm text-gray-500 font-medium uppercase tracking-wider">
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                <span>
                  {new Date(exp.startDate).toLocaleDateString()}
                  {" - "}
                  {exp.isCurrent
                    ? t("talent:experience.present")
                    : exp.endDate
                      ? new Date(exp.endDate).toLocaleDateString()
                      : ""}
                </span>
              </div>
            </div>
          </div>

          <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap border-t pt-4">
            {exp.description}
          </div>
        </div>
      </Card>
    </div>
  );
}
